import { useCallback, useMemo, useState } from "react";

import { MenuItemBadgeDto } from "services/menuItemBadges/restModels";
import { MenuItemBadgeRequestDto } from "services/menuItemBadges/restRequests";
import { useConstants } from "shared/hooks/useConstants";
import { useLocale } from "shared/hooks/useLocale";
import { useMenuItemBadges } from "shared/hooks/useMenuItemBadges";
import { useNotificationSnackbar } from "shared/ui";

import { useMenuItemBadgeMutations } from "features/MenuItemBadges/hooks/useMenuItemBadgeMutations";
import { MenuItemBadgesState } from "features/MenuItemBadges/types";

export const useMenuItemBadgesState = (): MenuItemBadgesState => {
    const locale = useLocale();

    const { badges, loading: badgesLoading, error: badgesError, setBadges } = useMenuItemBadges();
    const { constants, loading: constantsLoading, error: constantsError } = useConstants();

    const notify = useNotificationSnackbar();
    const { saving, saveBadge, removeBadge } = useMenuItemBadgeMutations(badges, setBadges, notify);

    const [isBadgeDialogOpen, setIsBadgeDialogOpen] = useState<boolean>(false);
    const [editedBadge, setEditedBadge] = useState<MenuItemBadgeDto | null>(null);
    const [badgeToDelete, setBadgeToDelete] = useState<MenuItemBadgeDto | null>(null);

    // Основной язык кабинета — первым, чтобы переключатель открывался на нём.
    const availableLanguages = useMemo(() => {
        const locales = (constants?.locales || []).map((item) => item.id);
        return [locale, ...locales.filter((item) => item !== locale)];
    }, [constants, locale]);

    const openCreateDialog = useCallback(() => {
        setEditedBadge(null);
        setIsBadgeDialogOpen(true);
    }, []);

    const openEditDialog = useCallback((badge: MenuItemBadgeDto) => {
        setEditedBadge(badge);
        setIsBadgeDialogOpen(true);
    }, []);

    const closeBadgeDialog = useCallback(() => {
        setIsBadgeDialogOpen(false);
        setEditedBadge(null);
    }, []);

    const submitBadge = useCallback(
        async (values: MenuItemBadgeRequestDto): Promise<boolean> => {
            const saved = await saveBadge(values, editedBadge ? editedBadge.id : null);

            if (!saved) return false;

            closeBadgeDialog();
            return true;
        },
        [closeBadgeDialog, editedBadge, saveBadge]
    );

    /*
    * Тумблер в списке — короткий путь «спрятать бейдж из меню»: он меняет только isEnabled,
    * поэтому остальные поля уезжают в запрос как есть.
    * */
    const toggleBadgeEnabled = useCallback(
        (badge: MenuItemBadgeDto) => {
            saveBadge(
                {
                    title: badge.title,
                    fill: badge.fill,
                    textColor: badge.textColor,
                    placement: badge.placement,
                    isEnabled: !badge.isEnabled,
                },
                badge.id
            );
        },
        [saveBadge]
    );

    const openDeleteDialog = useCallback((badge: MenuItemBadgeDto) => setBadgeToDelete(badge), []);
    const closeDeleteDialog = useCallback(() => setBadgeToDelete(null), []);

    const confirmDelete = useCallback(async () => {
        if (!badgeToDelete) return;

        const remaining = await removeBadge(badgeToDelete.id);

        if (remaining) setBadgeToDelete(null);
    }, [badgeToDelete, removeBadge]);

    return {
        badges,
        // Константы нужны переключателю языков — гейтим экран и на них.
        loading: badgesLoading || constantsLoading,
        error: badgesError || constantsError,
        saving,

        locale,
        availableLanguages,

        editedBadge,
        isBadgeDialogOpen,
        openCreateDialog,
        openEditDialog,
        closeBadgeDialog,
        submitBadge,
        toggleBadgeEnabled,

        badgeToDelete,
        openDeleteDialog,
        closeDeleteDialog,
        confirmDelete,

        notify,
    };
};
